import { useEffect, useLayoutEffect } from 'react';
import { Link, Outlet, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Dropdown, Result, Typography } from 'antd';
import {
  ArrowLeftOutlined,
  DashboardOutlined,
  LogoutOutlined,
  TeamOutlined,
  UserOutlined,
  WalletOutlined,
} from '@ant-design/icons';
import { useAuth } from '@/hooks/useAuth';
import {
  getListReturnPath,
  getMobileNavigation,
  getMobilePage,
  MOBILE_UNAVAILABLE_PATH,
} from './mobile-access';

const icons = {
  '/': <DashboardOutlined />,
  '/users': <UserOutlined />,
  '/organizations': <TeamOutlined />,
  '/credits': <WalletOutlined />,
};

export function MobileUnavailable() {
  return (
    <Result
      status="info"
      title="暂无可用的手机页面"
      subTitle="当前账号的功能需要在电脑端使用。你可以切换账号或联系管理员开通查看权限。"
    />
  );
}

export function MobileAdminLayout() {
  const { adminUser, permissions, logout, refreshMe } = useAuth();
  const location = useLocation();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const pages = getMobileNavigation(permissions);
  const current = getMobilePage(location.pathname);
  const activePath = current?.parent ?? current?.path;
  const supported = !!current || location.pathname === MOBILE_UNAVAILABLE_PATH;

  useLayoutEffect(() => {
    if (location.pathname !== '/users' && location.pathname !== '/organizations') {
      window.scrollTo(0, 0);
    }
  }, [location.pathname]);

  useEffect(() => {
    void refreshMe().catch(() => {});
  }, [refreshMe]);

  const handleLogout = async () => {
    try {
      await logout();
    } finally {
      navigate('/login', { replace: true });
    }
  };

  return (
    <div className="admin-mobile-shell">
      <header className="admin-mobile-header">
        <div className="flex min-w-0 items-center gap-2">
          {current?.parent && pages.some((page) => page.path === current.parent) && (
            <Button
              type="text"
              aria-label="返回列表"
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate(getListReturnPath(params.get('from'), current.parent!))}
            />
          )}
          <div className="min-w-0">
            <span className="block text-xs text-slate-500">Jointo 后台</span>
            <h1 className="m-0 text-lg font-semibold">{current?.label ?? '移动工作台'}</h1>
          </div>
        </div>
        <Dropdown
          trigger={['click']}
          menu={{
            items: [{ key: 'logout', label: '退出登录', icon: <LogoutOutlined />, danger: true }],
            onClick: () => {
              void handleLogout();
            },
          }}
        >
          <Button type="text" aria-label="管理员菜单" icon={<UserOutlined />}>
            <span className="inline-block max-w-24 truncate align-middle">
              {adminUser?.displayName || adminUser?.username || '管理员'}
            </span>
          </Button>
        </Dropdown>
      </header>
      <main className="admin-mobile-content">
        {supported ? (
          <Outlet context={{ refreshKey: 0 }} />
        ) : (
          <Result
            status="info"
            title="此功能请在电脑端使用"
            subTitle="手机端提供数据概览、用户和组织查询，以及算力充值。"
            extra={
              pages[0] ? (
                <Button type="primary" onClick={() => navigate(pages[0].path, { replace: true })}>
                  返回{pages[0].label}
                </Button>
              ) : (
                <Typography.Text type="secondary">当前账号暂无可用的手机页面</Typography.Text>
              )
            }
          />
        )}
      </main>
      {pages.length > 0 && (
        <nav className="admin-mobile-nav" aria-label="移动端导航">
          {pages.map((page) => (
            <Link
              key={page.path}
              to={page.path}
              aria-current={activePath === page.path ? 'page' : undefined}
            >
              {icons[page.path as keyof typeof icons]}
              <span>{page.label}</span>
            </Link>
          ))}
        </nav>
      )}
    </div>
  );
}
